const DEPLOYMENT_ENVIRONMENTS = Object.freeze([
  "development",
  "test",
  "staging",
  "production",
]);

const FIREBASE_ENV_FIELDS = Object.freeze({
  apiKey: "VUE_APP_FIREBASE_API_KEY",
  authDomain: "VUE_APP_FIREBASE_AUTH_DOMAIN",
  projectId: "VUE_APP_FIREBASE_PROJECT_ID",
  storageBucket: "VUE_APP_FIREBASE_STORAGE_BUCKET",
  messagingSenderId: "VUE_APP_FIREBASE_MESSAGING_SENDER_ID",
  appId: "VUE_APP_FIREBASE_APP_ID",
});

const SAFE_LOCAL_FIREBASE_CONFIG = Object.freeze({
  apiKey: "local-emulator-only",
  authDomain: "localhost",
  projectId: "demo-unit-local",
  storageBucket: "demo-unit-local.appspot.com",
  messagingSenderId: "000000000000",
  appId: "1:000000000000:web:0000000000000000000000",
});

const readValue = (value) => (typeof value === "string" ? value.trim() : "");
const APP_CHECK_SITE_KEY_PATTERN = /^[A-Za-z0-9_-]{20,256}$/;

const isReleaseEnvironment = (deploymentEnvironment) =>
  deploymentEnvironment === "staging" || deploymentEnvironment === "production";

export class RuntimeConfigurationError extends Error {
  constructor(message) {
    super(message);
    this.name = "RuntimeConfigurationError";
    this.code = "runtime-configuration-invalid";
  }
}

const configurationError = (message) => {
  throw new RuntimeConfigurationError(message);
};

export function resolveDeploymentEnvironment(environment = {}) {
  const nodeEnvironment = readValue(environment.NODE_ENV) || "development";
  const explicitEnvironment = readValue(environment.VUE_APP_DEPLOY_ENV);
  const deploymentEnvironment =
    explicitEnvironment ||
    (nodeEnvironment === "test" ? "test" : "development");

  if (!DEPLOYMENT_ENVIRONMENTS.includes(deploymentEnvironment)) {
    configurationError(
      "VUE_APP_DEPLOY_ENV must be development, test, staging, or production."
    );
  }

  if (
    nodeEnvironment === "production" &&
    !isReleaseEnvironment(deploymentEnvironment)
  ) {
    configurationError(
      "Production-mode bundles require VUE_APP_DEPLOY_ENV=staging or production."
    );
  }

  return deploymentEnvironment;
}

function validateFirebaseConfig(config) {
  if (!/^[a-z0-9][a-z0-9-]{3,28}[a-z0-9]$/.test(config.projectId)) {
    configurationError("VUE_APP_FIREBASE_PROJECT_ID is invalid.");
  }
  if (!/^\d{6,20}$/.test(config.messagingSenderId)) {
    configurationError("VUE_APP_FIREBASE_MESSAGING_SENDER_ID is invalid.");
  }
  if (!/^1:\d{6,20}:(?:web|android|ios):[A-Za-z0-9]+$/.test(config.appId)) {
    configurationError("VUE_APP_FIREBASE_APP_ID is invalid.");
  }
  return config;
}

export function resolveFirebaseConfig(environment = {}) {
  const deploymentEnvironment = resolveDeploymentEnvironment(environment);
  const values = Object.fromEntries(
    Object.entries(FIREBASE_ENV_FIELDS).map(([field, environmentKey]) => [
      field,
      readValue(environment[environmentKey]),
    ])
  );
  const providedFields = Object.entries(values).filter(([, value]) => value);

  if (
    providedFields.length === 0 &&
    !isReleaseEnvironment(deploymentEnvironment)
  ) {
    return { ...SAFE_LOCAL_FIREBASE_CONFIG };
  }

  const missingEnvironmentKeys = Object.entries(FIREBASE_ENV_FIELDS)
    .filter(([field]) => !values[field])
    .map(([, environmentKey]) => environmentKey);
  if (missingEnvironmentKeys.length > 0) {
    configurationError(
      `Firebase client configuration is incomplete; missing ${missingEnvironmentKeys.join(
        ", "
      )}.`
    );
  }

  if (isReleaseEnvironment(deploymentEnvironment)) {
    const expectedProjectId = readValue(
      environment.VUE_APP_FIREBASE_EXPECTED_PROJECT_ID
    );
    if (!expectedProjectId) {
      configurationError(
        "Release bundles require VUE_APP_FIREBASE_EXPECTED_PROJECT_ID."
      );
    }
    if (values.projectId !== expectedProjectId) {
      configurationError(
        "VUE_APP_FIREBASE_PROJECT_ID does not match VUE_APP_FIREBASE_EXPECTED_PROJECT_ID."
      );
    }
  }

  const measurementId = readValue(environment.VUE_APP_FIREBASE_MEASUREMENT_ID);
  const config = measurementId ? { ...values, measurementId } : values;
  return validateFirebaseConfig(config);
}

const resolveBooleanFlag = ({
  environment,
  key,
  defaultValue,
  deploymentEnvironment,
}) => {
  const value = readValue(environment[key]).toLowerCase();
  if (!value) return defaultValue;
  if (value === "true") return true;
  if (value === "false") return false;

  if (isReleaseEnvironment(deploymentEnvironment)) {
    configurationError(`${key} must be true or false.`);
  }
  return false;
};

export function resolveFeatureFlags(environment = {}) {
  const deploymentEnvironment = resolveDeploymentEnvironment(environment);
  const followEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_FOLLOW_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const digestEmailEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_DIGEST_EMAIL_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const trustedPublicationEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_TRUSTED_PUBLICATION_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const trustedMembershipEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_TRUSTED_MEMBERSHIP_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const orgContextEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORG_CONTEXT_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const orgPermissionReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORG_PERMISSION_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const orgMembershipCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORG_MEMBERSHIP_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const organisationAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORGANISATION_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const organisationAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORGANISATION_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (
    organisationAuthorityCommandsEnabled &&
    !organisationAuthorityReadsEnabled
  ) {
    configurationError(
      "VUE_APP_FEATURE_ORGANISATION_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_ORGANISATION_AUTHORITY_READS_ENABLED=true."
    );
  }
  if (
    organisationAuthorityCommandsEnabled &&
    (!orgContextEnabled || !orgPermissionReadsEnabled)
  ) {
    configurationError(
      "VUE_APP_FEATURE_ORGANISATION_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_ORG_CONTEXT_ENABLED=true and VUE_APP_FEATURE_ORG_PERMISSION_READS_ENABLED=true."
    );
  }
  const orgAppearanceEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORG_APPEARANCE_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const walletSummariesEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_WALLET_SUMMARIES_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const walletDetailReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_WALLET_DETAIL_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const walletStatementV3Enabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_WALLET_STATEMENT_V3_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const monetaryAuthorityQuotesEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MONETARY_AUTHORITY_QUOTES_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const monetaryAuthoritySettlementEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MONETARY_AUTHORITY_SETTLEMENT_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const organisationCreationEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ORGANISATION_CREATION_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const itemAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ITEMS_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const itemAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ITEMS_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const mediaAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MEDIA_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const mediaAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MEDIA_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (mediaAuthorityCommandsEnabled && !mediaAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_MEDIA_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_MEDIA_AUTHORITY_READS_ENABLED=true."
    );
  }
  if (itemAuthorityCommandsEnabled && !itemAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_ITEMS_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_ITEMS_AUTHORITY_READS_ENABLED=true."
    );
  }
  const identityAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_IDENTITY_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const identityAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_IDENTITY_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (identityAuthorityCommandsEnabled && !identityAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_IDENTITY_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_IDENTITY_AUTHORITY_READS_ENABLED=true."
    );
  }
  const storageAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_STORAGE_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const storageAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_STORAGE_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (storageAuthorityCommandsEnabled && !storageAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_STORAGE_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_STORAGE_AUTHORITY_READS_ENABLED=true."
    );
  }
  const accountSelfDeletionEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ACCOUNT_SELF_DELETION_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const accountAdministrationEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_ACCOUNT_ADMINISTRATION_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const apiKeyAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_API_KEY_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const apiKeyAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_API_KEY_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const externalWalletAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const externalWalletAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (
    externalWalletAuthorityCommandsEnabled &&
    !externalWalletAuthorityReadsEnabled
  ) {
    configurationError(
      "VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_READS_ENABLED=true."
    );
  }
  const walletAuthenticationEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_WALLET_AUTHENTICATION_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (
    walletAuthenticationEnabled &&
    !APP_CHECK_SITE_KEY_PATTERN.test(
      readValue(environment.VUE_APP_FIREBASE_APP_CHECK_SITE_KEY),
    )
  ) {
    configurationError(
      "VUE_APP_FEATURE_WALLET_AUTHENTICATION_ENABLED requires a valid VUE_APP_FIREBASE_APP_CHECK_SITE_KEY."
    );
  }
  const followingFeedEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_FOLLOWING_FEED_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const followingAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_FOLLOWING_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const followingAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_FOLLOWING_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (followingAuthorityCommandsEnabled && !followingAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_FOLLOWING_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_FOLLOWING_AUTHORITY_READS_ENABLED=true."
    );
  }
  const messagesAuthorityReadsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MESSAGES_AUTHORITY_READS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  const messagesAuthorityCommandsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_MESSAGES_AUTHORITY_COMMANDS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (messagesAuthorityCommandsEnabled && !messagesAuthorityReadsEnabled) {
    configurationError(
      "VUE_APP_FEATURE_MESSAGES_AUTHORITY_COMMANDS_ENABLED requires VUE_APP_FEATURE_MESSAGES_AUTHORITY_READS_ENABLED=true."
    );
  }
  const siblingRelationshipsEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FEATURE_SIBLING_RELATIONSHIPS_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });
  if (
    siblingRelationshipsEnabled &&
    (!organisationAuthorityReadsEnabled ||
      !organisationAuthorityCommandsEnabled ||
      !messagesAuthorityReadsEnabled ||
      !messagesAuthorityCommandsEnabled)
  ) {
    configurationError(
      "VUE_APP_FEATURE_SIBLING_RELATIONSHIPS_ENABLED requires organisation and Messages authority reads and commands."
    );
  }
  if (monetaryAuthoritySettlementEnabled && !monetaryAuthorityQuotesEnabled) {
    configurationError(
      "VUE_APP_FEATURE_MONETARY_AUTHORITY_SETTLEMENT_ENABLED requires VUE_APP_FEATURE_MONETARY_AUTHORITY_QUOTES_ENABLED=true."
    );
  }
  const persistentFirestoreCacheEnabled = resolveBooleanFlag({
    environment,
    key: "VUE_APP_FIRESTORE_PERSISTENT_CACHE_ENABLED",
    defaultValue: false,
    deploymentEnvironment,
  });

  return Object.freeze({
    followEnabled,
    digestEmailEnabled,
    trustedPublicationEnabled,
    trustedMembershipEnabled,
    orgContextEnabled,
    orgPermissionReadsEnabled,
    orgMembershipCommandsEnabled,
    organisationAuthorityReadsEnabled,
    organisationAuthorityCommandsEnabled,
    orgAppearanceEnabled,
    walletSummariesEnabled,
    walletDetailReadsEnabled,
    walletStatementV3Enabled,
    monetaryAuthorityQuotesEnabled,
    monetaryAuthoritySettlementEnabled,
    organisationCreationEnabled,
    itemAuthorityReadsEnabled,
    itemAuthorityCommandsEnabled,
    mediaAuthorityReadsEnabled,
    mediaAuthorityCommandsEnabled,
    identityAuthorityReadsEnabled,
    identityAuthorityCommandsEnabled,
    storageAuthorityReadsEnabled,
    storageAuthorityCommandsEnabled,
    accountSelfDeletionEnabled,
    accountAdministrationEnabled,
    apiKeyAuthorityReadsEnabled,
    apiKeyAuthorityCommandsEnabled,
    externalWalletAuthorityReadsEnabled,
    externalWalletAuthorityCommandsEnabled,
    walletAuthenticationEnabled,
    followingFeedEnabled,
    followingAuthorityReadsEnabled,
    followingAuthorityCommandsEnabled,
    messagesAuthorityReadsEnabled,
    messagesAuthorityCommandsEnabled,
    siblingRelationshipsEnabled,
    persistentFirestoreCacheEnabled,
    followUiEnabled: followEnabled,
    digestUiEnabled: followEnabled && digestEmailEnabled,
  });
}

const injectedEnvironment = Object.freeze({
  NODE_ENV: process.env.NODE_ENV,
  VUE_APP_DEPLOY_ENV: process.env.VUE_APP_DEPLOY_ENV,
  VUE_APP_FIREBASE_API_KEY: process.env.VUE_APP_FIREBASE_API_KEY,
  VUE_APP_FIREBASE_AUTH_DOMAIN: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  VUE_APP_FIREBASE_PROJECT_ID: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  VUE_APP_FIREBASE_STORAGE_BUCKET: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  VUE_APP_FIREBASE_MESSAGING_SENDER_ID:
    process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  VUE_APP_FIREBASE_APP_ID: process.env.VUE_APP_FIREBASE_APP_ID,
  VUE_APP_FIREBASE_MEASUREMENT_ID: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
  VUE_APP_FIREBASE_EXPECTED_PROJECT_ID:
    process.env.VUE_APP_FIREBASE_EXPECTED_PROJECT_ID,
  VUE_APP_FIREBASE_APP_CHECK_SITE_KEY:
    process.env.VUE_APP_FIREBASE_APP_CHECK_SITE_KEY,
  VUE_APP_FEATURE_FOLLOW_ENABLED: process.env.VUE_APP_FEATURE_FOLLOW_ENABLED,
  VUE_APP_FEATURE_DIGEST_EMAIL_ENABLED:
    process.env.VUE_APP_FEATURE_DIGEST_EMAIL_ENABLED,
  VUE_APP_FEATURE_TRUSTED_PUBLICATION_ENABLED:
    process.env.VUE_APP_FEATURE_TRUSTED_PUBLICATION_ENABLED,
  VUE_APP_FEATURE_TRUSTED_MEMBERSHIP_ENABLED:
    process.env.VUE_APP_FEATURE_TRUSTED_MEMBERSHIP_ENABLED,
  VUE_APP_FEATURE_ORG_CONTEXT_ENABLED:
    process.env.VUE_APP_FEATURE_ORG_CONTEXT_ENABLED,
  VUE_APP_FEATURE_ORG_PERMISSION_READS_ENABLED:
    process.env.VUE_APP_FEATURE_ORG_PERMISSION_READS_ENABLED,
  VUE_APP_FEATURE_ORG_MEMBERSHIP_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_ORG_MEMBERSHIP_COMMANDS_ENABLED,
  VUE_APP_FEATURE_ORGANISATION_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_ORGANISATION_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_ORGANISATION_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_ORGANISATION_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_ORG_APPEARANCE_ENABLED:
    process.env.VUE_APP_FEATURE_ORG_APPEARANCE_ENABLED,
  VUE_APP_FEATURE_WALLET_SUMMARIES_ENABLED:
    process.env.VUE_APP_FEATURE_WALLET_SUMMARIES_ENABLED,
  VUE_APP_FEATURE_WALLET_DETAIL_READS_ENABLED:
    process.env.VUE_APP_FEATURE_WALLET_DETAIL_READS_ENABLED,
  VUE_APP_FEATURE_WALLET_STATEMENT_V3_ENABLED:
    process.env.VUE_APP_FEATURE_WALLET_STATEMENT_V3_ENABLED,
  VUE_APP_FEATURE_MONETARY_AUTHORITY_QUOTES_ENABLED:
    process.env.VUE_APP_FEATURE_MONETARY_AUTHORITY_QUOTES_ENABLED,
  VUE_APP_FEATURE_MONETARY_AUTHORITY_SETTLEMENT_ENABLED:
    process.env.VUE_APP_FEATURE_MONETARY_AUTHORITY_SETTLEMENT_ENABLED,
  VUE_APP_FEATURE_ORGANISATION_CREATION_ENABLED:
    process.env.VUE_APP_FEATURE_ORGANISATION_CREATION_ENABLED,
  VUE_APP_FEATURE_ITEMS_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_ITEMS_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_ITEMS_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_ITEMS_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_MEDIA_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_MEDIA_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_MEDIA_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_MEDIA_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_IDENTITY_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_IDENTITY_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_IDENTITY_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_IDENTITY_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_STORAGE_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_STORAGE_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_STORAGE_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_STORAGE_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_ACCOUNT_SELF_DELETION_ENABLED:
    process.env.VUE_APP_FEATURE_ACCOUNT_SELF_DELETION_ENABLED,
  VUE_APP_FEATURE_ACCOUNT_ADMINISTRATION_ENABLED:
    process.env.VUE_APP_FEATURE_ACCOUNT_ADMINISTRATION_ENABLED,
  VUE_APP_FEATURE_API_KEY_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_API_KEY_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_API_KEY_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_API_KEY_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_EXTERNAL_WALLET_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_WALLET_AUTHENTICATION_ENABLED:
    process.env.VUE_APP_FEATURE_WALLET_AUTHENTICATION_ENABLED,
  VUE_APP_FEATURE_FOLLOWING_FEED_ENABLED:
    process.env.VUE_APP_FEATURE_FOLLOWING_FEED_ENABLED,
  VUE_APP_FEATURE_FOLLOWING_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_FOLLOWING_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_FOLLOWING_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_FOLLOWING_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_MESSAGES_AUTHORITY_READS_ENABLED:
    process.env.VUE_APP_FEATURE_MESSAGES_AUTHORITY_READS_ENABLED,
  VUE_APP_FEATURE_MESSAGES_AUTHORITY_COMMANDS_ENABLED:
    process.env.VUE_APP_FEATURE_MESSAGES_AUTHORITY_COMMANDS_ENABLED,
  VUE_APP_FEATURE_SIBLING_RELATIONSHIPS_ENABLED:
    process.env.VUE_APP_FEATURE_SIBLING_RELATIONSHIPS_ENABLED,
  VUE_APP_FIRESTORE_PERSISTENT_CACHE_ENABLED:
    process.env.VUE_APP_FIRESTORE_PERSISTENT_CACHE_ENABLED,
});

export const deploymentEnvironment =
  resolveDeploymentEnvironment(injectedEnvironment);
export const firebaseConfig = Object.freeze(
  resolveFirebaseConfig(injectedEnvironment)
);
export const featureFlags = resolveFeatureFlags(injectedEnvironment);
export const firebaseAppCheckSiteKey = readValue(
  injectedEnvironment.VUE_APP_FIREBASE_APP_CHECK_SITE_KEY,
);
export const isFollowFeatureEnabled = featureFlags.followEnabled;
export const isDigestEmailFeatureEnabled = featureFlags.digestEmailEnabled;
export const isTrustedPublicationFeatureEnabled =
  featureFlags.trustedPublicationEnabled;
export const isTrustedMembershipFeatureEnabled =
  featureFlags.trustedMembershipEnabled;
export const isOrgContextFeatureEnabled = featureFlags.orgContextEnabled;
export const isOrgPermissionReadsFeatureEnabled =
  featureFlags.orgPermissionReadsEnabled;
export const isOrgMembershipCommandsFeatureEnabled =
  featureFlags.orgMembershipCommandsEnabled;
export const isOrganisationAuthorityReadsFeatureEnabled =
  featureFlags.organisationAuthorityReadsEnabled;
export const isOrganisationAuthorityCommandsFeatureEnabled =
  featureFlags.organisationAuthorityCommandsEnabled;
export const isOrgAppearanceFeatureEnabled = featureFlags.orgAppearanceEnabled;
export const isOwnWalletSummariesFeatureEnabled =
  featureFlags.walletSummariesEnabled;
export const isOwnWalletDetailFeatureEnabled =
  featureFlags.walletDetailReadsEnabled;
// The combined statement is a newer request version of the Wallet detail
// read, so it is only ever used while that read itself is enabled.
export const isWalletStatementV3FeatureEnabled =
  featureFlags.walletDetailReadsEnabled &&
  featureFlags.walletStatementV3Enabled;
export const isMonetaryAuthorityQuotesFeatureEnabled =
  featureFlags.monetaryAuthorityQuotesEnabled;
export const isMonetaryAuthoritySettlementFeatureEnabled =
  featureFlags.monetaryAuthoritySettlementEnabled;
export const isOrganisationCreationFeatureEnabled =
  featureFlags.organisationCreationEnabled;
export const isItemAuthorityReadsFeatureEnabled =
  featureFlags.itemAuthorityReadsEnabled;
export const isItemAuthorityCommandsFeatureEnabled =
  featureFlags.itemAuthorityCommandsEnabled;
export const isMediaAuthorityReadsFeatureEnabled =
  featureFlags.mediaAuthorityReadsEnabled;
export const isMediaAuthorityCommandsFeatureEnabled =
  featureFlags.mediaAuthorityCommandsEnabled;
export const isIdentityAuthorityReadsFeatureEnabled =
  featureFlags.identityAuthorityReadsEnabled;
export const isIdentityAuthorityCommandsFeatureEnabled =
  featureFlags.identityAuthorityCommandsEnabled;
export const isStorageAuthorityReadsFeatureEnabled =
  featureFlags.storageAuthorityReadsEnabled;
export const isStorageAuthorityCommandsFeatureEnabled =
  featureFlags.storageAuthorityCommandsEnabled;
export const isAccountSelfDeletionFeatureEnabled =
  featureFlags.accountSelfDeletionEnabled;
export const isAccountAdministrationFeatureEnabled =
  featureFlags.accountAdministrationEnabled;
export const isApiKeyAuthorityReadsFeatureEnabled =
  featureFlags.apiKeyAuthorityReadsEnabled;
export const isApiKeyAuthorityCommandsFeatureEnabled =
  featureFlags.apiKeyAuthorityCommandsEnabled;
export const isExternalWalletAuthorityReadsFeatureEnabled =
  featureFlags.externalWalletAuthorityReadsEnabled;
export const isExternalWalletAuthorityCommandsFeatureEnabled =
  featureFlags.externalWalletAuthorityCommandsEnabled;
export const isWalletAuthenticationFeatureEnabled =
  featureFlags.walletAuthenticationEnabled;
export const isFollowingFeedFeatureEnabled = featureFlags.followingFeedEnabled;
export const isFollowingAuthorityReadsFeatureEnabled =
  featureFlags.followingAuthorityReadsEnabled;
export const isFollowingAuthorityCommandsFeatureEnabled =
  featureFlags.followingAuthorityCommandsEnabled;
export const isMessagesAuthorityReadsFeatureEnabled =
  featureFlags.messagesAuthorityReadsEnabled;
export const isMessagesAuthorityCommandsFeatureEnabled =
  featureFlags.messagesAuthorityCommandsEnabled;
export const isSiblingRelationshipsFeatureEnabled =
  featureFlags.siblingRelationshipsEnabled;
export const isPersistentFirestoreCacheEnabled =
  featureFlags.persistentFirestoreCacheEnabled;
export const isFollowUiEnabled = featureFlags.followUiEnabled;
export const isDigestUiEnabled = featureFlags.digestUiEnabled;

// Wishlist persistence has no private-authority contract yet. This is not an
// environment switch: the UI stays unavailable until that authority exists.
export const isWishlistFeatureEnabled = false;
